import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertTriangle, Check, CheckCircle2, Copy, Download, FileText, History, Info, Loader2, Lock, Play,
  RefreshCw, TerminalSquare, Trash2, X, XCircle,
} from "lucide-react";
import DashboardLayout from "../components/DashboardLayout";
import Modal from "../components/Modal";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/context/ToastContext";
import { apiErrorMessage, systemConsoleApi } from "@/lib/api";

// ─── Types (App\Support\SystemConsole / SystemHealth / SystemLog) ────────────

type Level = "safe" | "caution" | "danger";
type State = "ok" | "warn" | "bad" | "info";

interface Command {
  key: string;
  group: string;
  title: string;
  help: string;
  level: Level;
  confirm: string | null;
  backup: boolean;
  advanced: boolean;
  artisan: string;
}
interface Catalogue {
  groups: { key: string; label: string }[];
  commands: Command[];
  update_steps: string[];
}
interface HealthItem { label: string; value: string; state: State; hint: string | null }
interface Health {
  summary: { app_name: string; environment: string; is_production: boolean; maintenance: boolean; secure: boolean };
  sections: { title: string; items: HealthItem[] }[];
  backups: { name: string; size: number; created_at: string }[];
}
interface RunResult {
  id: string; at: string; key: string; title: string; artisan: string;
  ok: boolean; exit_code: number; duration_ms: number; backup: string | null; output: string;
}
interface HistoryEntry extends Partial<RunResult> {
  id: string; at: string; key: string; title: string; ok: boolean;
  user?: { id: number; name: string; email: string } | null;
  ip?: string;
}
interface LogData {
  file: string;
  size: number;
  entries: { time: string | null; level: string; message: string; detail: string }[];
}

/** super_admin and master; the API allows the same roles. */
const CONSOLE_ROLES = ["super_admin", "master"];
const TABS = [
  { key: "health", label: "Health" },
  { key: "commands", label: "Commands" },
  { key: "history", label: "History" },
  { key: "log", label: "Error log" },
] as const;
type TabKey = (typeof TABS)[number]["key"];

const when = (iso?: string | null) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString("en-IN", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" });
};
const seconds = (ms?: number) => (ms === undefined ? "" : ms < 1000 ? `${ms} ms` : `${(ms / 1000).toFixed(1)} s`);
const bytes = (b: number) => (b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(1)} KB` : `${(b / 1048576).toFixed(1)} MB`);

// ─── Output panel state ──────────────────────────────────────────────────────

interface Output {
  title: string;
  text: string;
  state: "running" | "ok" | "failed";
  meta: string;
}

// ─── Page ────────────────────────────────────────────────────────────────────

export default function SystemConsolePage() {
  const { user } = useAuth();
  const allowed = !!user && CONSOLE_ROLES.includes(user.role);

  return (
    <DashboardLayout>
      {allowed ? <SystemConsole /> : (
        <div className="max-w-md mx-auto mt-10 bg-white rounded-xl border border-stone-200 p-6 text-center">
          <div className="w-11 h-11 rounded-xl bg-stone-100 flex items-center justify-center mx-auto mb-3">
            <Lock className="w-5 h-5 text-stone-500" />
          </div>
          <p className="text-sm font-semibold text-stone-800">Only the super admin can use the System Console</p>
          <p className="text-xs text-stone-500 mt-1">It runs maintenance commands on the server, such as database updates.</p>
        </div>
      )}
    </DashboardLayout>
  );
}

/** The console itself (exported for previews); the page adds the layout and role check. */
export function SystemConsole() {
  const { showToast } = useToast();
  const [params, setParams] = useSearchParams();
  const tab: TabKey = (TABS.find((t) => t.key === params.get("tab"))?.key ?? "health");
  const setTab = (key: TabKey) => setParams(key === "health" ? {} : { tab: key }, { replace: true });

  const [catalogue, setCatalogue] = useState<Catalogue | null>(null);
  const [catalogueError, setCatalogueError] = useState<string | null>(null);
  const [health, setHealth] = useState<Health | null>(null);
  const [healthError, setHealthError] = useState<string | null>(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [historyVersion, setHistoryVersion] = useState(0);
  const [output, setOutput] = useState<Output | null>(null);
  const [running, setRunning] = useState(false);
  const [asking, setAsking] = useState<{ title: string; text: string; word: string | null; ok: string; danger: boolean; resolve: (v: string | false) => void } | null>(null);

  const loadCatalogue = useCallback(async () => {
    setCatalogueError(null);
    try {
      setCatalogue((await systemConsoleApi.commands()).data);
    } catch (e) {
      setCatalogueError(apiErrorMessage(e, "Could not load the commands."));
    }
  }, []);

  const loadHealth = useCallback(async () => {
    setHealthLoading(true);
    setHealthError(null);
    try {
      setHealth((await systemConsoleApi.status()).data);
    } catch (e) {
      setHealthError(apiErrorMessage(e, "Could not check the server."));
    } finally {
      setHealthLoading(false);
    }
  }, []);

  useEffect(() => { loadCatalogue(); loadHealth(); }, [loadCatalogue, loadHealth]);

  /** Opens the confirm dialog; resolves to the typed word (or "yes"), or false when cancelled. */
  const ask = (title: string, text: string, word: string | null, ok = "Run", danger = false) =>
    new Promise<string | false>((resolve) => setAsking({ title, text, word, ok, danger, resolve }));

  const runCommand = async (c: Command, opts: { asked?: boolean; append?: boolean } = {}): Promise<RunResult | null> => {
    if (running) {
      showToast("Another command is still running.", "error");
      return null;
    }
    let word: string | null = null;
    if (!opts.asked && c.level !== "safe") {
      const answer = await ask(
        `${c.title}?`,
        [c.help, c.backup && !/backed up/i.test(c.help) ? "The database is backed up first." : ""].filter(Boolean).join("\n\n"),
        c.confirm, "Run", c.level === "danger",
      );
      if (answer === false) return null;
      word = c.confirm ? answer : null;
    }

    setRunning(true);
    const header = `$ ${c.artisan}\n`;
    setOutput((prev) => ({
      title: c.title,
      text: opts.append && prev ? `${prev.text}\n${header}` : header,
      state: "running",
      meta: "Running… keep this page open",
    }));
    try {
      const r: RunResult = (await systemConsoleApi.run(c.key, word)).data;
      setOutput((prev) => ({
        title: c.title,
        text: `${prev?.text ?? header}${r.output}\n`,
        state: r.ok ? "ok" : "failed",
        meta: `${r.ok ? "Finished" : "Failed"} in ${seconds(r.duration_ms)}${r.backup ? ` · backup ${r.backup}` : ""}`,
      }));
      return r;
    } catch (e) {
      setOutput((prev) => ({
        title: c.title,
        text: `${prev?.text ?? header}${apiErrorMessage(e, "The command did not run.")}\n`,
        state: "failed",
        meta: "Did not finish",
      }));
      return null;
    } finally {
      setRunning(false);
      setHistoryVersion((v) => v + 1);
      loadHealth();
    }
  };

  const runByKey = (key: string) => {
    const c = catalogue?.commands.find((x) => x.key === key);
    if (c) runCommand(c);
  };

  const updateSteps = useMemo(
    () => (catalogue?.update_steps ?? []).map((k) => catalogue?.commands.find((c) => c.key === k)).filter(Boolean) as Command[],
    [catalogue],
  );

  const runUpdate = async () => {
    const ok = await ask(
      "Run the update steps?",
      updateSteps.map((c, i) => `${i + 1}. ${c.title}`).join("\n") + "\n\nThe database is backed up before it is updated.",
      null, "Run all",
    );
    if (ok === false) return;
    for (let i = 0; i < updateSteps.length; i++) {
      const r = await runCommand(updateSteps[i], { asked: true, append: i > 0 });
      if (!r || !r.ok) {
        setOutput((prev) => prev && { ...prev, text: `${prev.text}\nStopped at step ${i + 1} of ${updateSteps.length}: ${updateSteps[i].title}.\n`, state: "failed" });
        return;
      }
    }
    setOutput((prev) => prev && { ...prev, title: "Update steps", state: "ok", meta: `All ${updateSteps.length} steps finished` });
  };

  const s = health?.summary;

  return (
    <div className={`space-y-4 ${output ? "pb-[45vh]" : ""}`}>
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2 min-w-0">
          <TerminalSquare className="w-5 h-5 mt-0.5 text-amber-600 shrink-0" />
          <div className="min-w-0">
            <h1 className="text-lg font-bold text-stone-800">System Console</h1>
            <p className="text-xs text-stone-500">Server maintenance without SSH: database updates, caches, uploaded files and the error log</p>
          </div>
        </div>
        {s && (
          <span className={`shrink-0 text-[11px] font-bold uppercase tracking-wide px-2 py-1 rounded-full border ${s.is_production ? "bg-red-50 text-red-700 border-red-200" : "bg-stone-100 text-stone-600 border-stone-200"}`}>
            {s.is_production ? "Live server" : s.environment}
          </span>
        )}
      </div>

      {s?.maintenance && (
        <div className="flex flex-col sm:flex-row sm:items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          <AlertTriangle className="w-4 h-4 shrink-0 hidden sm:block" />
          <p className="flex-1">The site is in maintenance mode: visitors and staff see “back soon”.</p>
          <button type="button" onClick={() => runByKey("up")} disabled={running}
            className="h-10 px-4 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50">
            Bring it back online
          </button>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto bg-white border border-stone-200 rounded-xl p-1" role="tablist">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tab === t.key}
            onClick={() => setTab(t.key)}
            className={`flex-1 shrink-0 h-10 px-3 rounded-lg text-sm font-semibold whitespace-nowrap ${tab === t.key ? "bg-amber-50 text-amber-800" : "text-stone-500 hover:text-stone-700"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "health" && (
        <HealthTab
          health={health}
          loading={healthLoading}
          error={healthError}
          onRetry={loadHealth}
          updateSteps={updateSteps}
          onRunUpdate={runUpdate}
          running={running}
        />
      )}
      {tab === "commands" && (
        catalogueError ? <ErrorCard message={catalogueError} onRetry={loadCatalogue} />
          : !catalogue ? <Skeleton rows={3} />
          : <CommandsTab catalogue={catalogue} running={running} onRun={(c) => runCommand(c)} />
      )}
      {tab === "history" && <HistoryTab version={historyVersion} />}
      {tab === "log" && <LogTab ask={ask} />}

      {output && <OutputPanel output={output} running={running} onClose={() => setOutput(null)} />}

      <ConfirmDialog asking={asking} onDone={(v) => { asking?.resolve(v); setAsking(null); }} />
    </div>
  );
}

// ─── Health ──────────────────────────────────────────────────────────────────

function StateIcon({ state }: { state: State }) {
  if (state === "ok") return <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" aria-label="OK" />;
  if (state === "warn") return <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" aria-label="Needs a look" />;
  if (state === "bad") return <XCircle className="w-5 h-5 text-red-600 shrink-0" aria-label="Problem" />;
  return <Info className="w-5 h-5 text-stone-300 shrink-0" aria-label="Information" />;
}

function HealthTab({ health, loading, error, onRetry, updateSteps, onRunUpdate, running }: {
  health: Health | null; loading: boolean; error: string | null; onRetry: () => void;
  updateSteps: Command[]; onRunUpdate: () => void; running: boolean;
}) {
  if (error) return <ErrorCard message={error} onRetry={onRetry} />;
  if (!health) return <Skeleton rows={4} />;
  const problems = health.sections.flatMap((x) => x.items).filter((i) => i.state === "bad" || i.state === "warn").length;

  return (
    <div className="space-y-4">
      <section className="bg-white rounded-xl border border-stone-200 p-4">
        <h2 className="text-sm font-semibold text-stone-800">After uploading a new version</h2>
        <p className="text-xs text-stone-500 mt-0.5">Runs these one after another and stops if one fails:</p>
        <ol className="list-decimal pl-5 my-3 space-y-1 text-sm text-stone-700">
          {updateSteps.map((c) => (
            <li key={c.key}>{c.title}{c.backup && <span className="text-xs text-stone-500"> (backs up first)</span>}</li>
          ))}
        </ol>
        <button type="button" onClick={onRunUpdate} disabled={running || updateSteps.length === 0}
          className="h-10 w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold disabled:opacity-50">
          <Play className="w-4 h-4" /> Run update steps
        </button>
      </section>

      <div className="flex items-center justify-between gap-2">
        <p className={`text-sm font-medium ${problems ? "text-amber-700" : "text-emerald-700"}`}>
          {problems ? `${problems} thing${problems === 1 ? "" : "s"} to look at` : "Everything looks fine"}
        </p>
        <button type="button" onClick={onRetry} disabled={loading}
          className="h-10 inline-flex items-center gap-2 px-3 rounded-xl border border-stone-200 bg-white text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50">
          <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Check again
        </button>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        {health.sections.map((sec) => (
          <section key={sec.title} className="bg-white rounded-xl border border-stone-200 p-4">
            <h2 className="text-sm font-semibold text-stone-800 mb-1">{sec.title}</h2>
            <ul className="divide-y divide-stone-100">
              {sec.items.map((i) => (
                <li key={i.label} className="py-2.5 flex gap-2.5">
                  <StateIcon state={i.state} />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap justify-between gap-x-3">
                      <span className="text-sm font-medium text-stone-700">{i.label}</span>
                      <span className="text-sm text-stone-500 break-words">{i.value}</span>
                    </div>
                    {i.hint && <p className="text-xs text-stone-500 mt-0.5">{i.hint}</p>}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        ))}
        <section className="bg-white rounded-xl border border-stone-200 p-4">
          <h2 className="text-sm font-semibold text-stone-800 mb-1">Latest backups</h2>
          {health.backups.length === 0 ? (
            <p className="text-sm text-stone-500 py-2">No backups yet. “Run database updates” makes one automatically.</p>
          ) : (
            <ul className="divide-y divide-stone-100">
              {health.backups.map((b) => (
                <li key={b.name} className="py-2.5 flex gap-2.5">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap justify-between gap-x-3">
                      <span className="text-xs font-mono text-stone-700 break-all">{b.name}</span>
                      <span className="text-sm text-stone-500">{bytes(b.size)}</span>
                    </div>
                    <p className="text-xs text-stone-500">{when(b.created_at)} · storage/app/backups on the server</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

// ─── Commands ────────────────────────────────────────────────────────────────

function CommandsTab({ catalogue, running, onRun }: { catalogue: Catalogue; running: boolean; onRun: (c: Command) => void }) {
  const [advanced, setAdvanced] = useState(false);
  const groups = catalogue.groups
    .map((g) => ({ ...g, commands: catalogue.commands.filter((c) => c.group === g.key && (advanced || !c.advanced)) }))
    .filter((g) => g.commands.length > 0);

  return (
    <div className="space-y-4">
      {groups.map((g) => (
        <section key={g.key}>
          <h2 className="text-xs font-bold uppercase tracking-wide text-stone-500 mb-2">{g.label}</h2>
          <div className="bg-white rounded-xl border border-stone-200 divide-y divide-stone-100">
            {g.commands.map((c) => (
              <div key={c.key} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <span className="text-sm font-semibold text-stone-800">{c.title}</span>
                    {c.level === "caution" && <Tag className="bg-amber-50 text-amber-700">Asks first</Tag>}
                    {c.level === "danger" && <Tag className="bg-red-50 text-red-700">Careful</Tag>}
                    {c.backup && <Tag className="bg-emerald-50 text-emerald-700">Backs up first</Tag>}
                  </div>
                  {c.help && <p className="text-xs text-stone-500 mt-1">{c.help}</p>}
                  <code className="inline-block mt-1.5 text-[11px] text-stone-500 bg-stone-50 border border-stone-200 rounded px-1.5 py-0.5 break-all">{c.artisan}</code>
                </div>
                <button
                  type="button"
                  onClick={() => onRun(c)}
                  disabled={running}
                  className={`h-10 sm:w-28 shrink-0 inline-flex items-center justify-center gap-1.5 px-4 rounded-xl text-sm font-semibold disabled:opacity-50 ${
                    c.level === "danger" ? "bg-red-600 hover:bg-red-700 text-white"
                      : c.level === "caution" ? "bg-amber-600 hover:bg-amber-700 text-white"
                      : "border border-stone-200 bg-white text-stone-700 hover:bg-stone-50"
                  }`}
                >
                  <Play className="w-3.5 h-3.5" /> Run
                </button>
              </div>
            ))}
          </div>
        </section>
      ))}
      <label className="flex items-center gap-2 min-h-10 text-sm text-stone-600 cursor-pointer">
        <input type="checkbox" checked={advanced} onChange={(e) => setAdvanced(e.target.checked)} className="w-4 h-4 accent-amber-600" />
        Show advanced commands
      </label>
      <p className="text-xs text-stone-500">
        Commands that erase the whole database (migrate:fresh, migrate:reset, db:wipe) and key:generate are not offered here on purpose.
      </p>
    </div>
  );
}

function Tag({ className, children }: { className: string; children: ReactNode }) {
  return <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${className}`}>{children}</span>;
}

// ─── History ─────────────────────────────────────────────────────────────────

function HistoryTab({ version }: { version: number }) {
  const [entries, setEntries] = useState<HistoryEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    setError(null);
    systemConsoleApi.history()
      .then((res) => { if (alive) setEntries(res.data); })
      .catch((e) => { if (alive) setError(apiErrorMessage(e, "Could not load the history.")); });
    return () => { alive = false; };
  }, [version, attempt]);

  if (error) return <ErrorCard message={error} onRetry={() => setAttempt((n) => n + 1)} />;
  if (!entries) return <Skeleton rows={2} />;

  return (
    <section className="bg-white rounded-xl border border-stone-200 p-4">
      <div className="flex items-center gap-2 mb-1">
        <History className="w-4 h-4 text-stone-500" />
        <h2 className="text-sm font-semibold text-stone-800">What was run</h2>
      </div>
      <p className="text-xs text-stone-500 mb-2">The last 50 commands, newest first. Passwords in the output are hidden.</p>
      {entries.length === 0 ? (
        <p className="text-sm text-stone-400 py-8 text-center">Nothing has been run yet.</p>
      ) : (
        <ul className="divide-y divide-stone-100">
          {entries.map((h) => (
            <li key={h.id} className="py-3 flex gap-2.5">
              {h.ok ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" /> : <XCircle className="w-5 h-5 text-red-600 shrink-0" />}
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap justify-between gap-x-3">
                  <span className="text-sm font-semibold text-stone-800">{h.title}</span>
                  <span className="text-xs text-stone-500">{when(h.at)}</span>
                </div>
                <p className="text-xs text-stone-500 mt-0.5 break-words">
                  {[h.user?.name ? `by ${h.user.name}` : null, h.artisan, seconds(h.duration_ms), h.ip ? `from ${h.ip}` : null, h.backup ? `backup ${h.backup}` : null]
                    .filter(Boolean).join(" · ")}
                </p>
                {h.output && (
                  <details className="mt-1.5">
                    <summary className="text-xs font-semibold text-amber-700 cursor-pointer min-h-8 flex items-center">Show output</summary>
                    <pre className="mt-1 bg-stone-900 text-stone-100 rounded-lg p-3 text-xs leading-relaxed whitespace-pre-wrap break-words max-h-96 overflow-auto">{h.output}</pre>
                  </details>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── Error log ───────────────────────────────────────────────────────────────

const LEVEL_CLASS: Record<string, string> = {
  ERROR: "bg-red-50 text-red-700", CRITICAL: "bg-red-50 text-red-700", ALERT: "bg-red-50 text-red-700", EMERGENCY: "bg-red-50 text-red-700",
  WARNING: "bg-amber-50 text-amber-700",
};

function LogTab({ ask }: { ask: (title: string, text: string, word: string | null, ok?: string, danger?: boolean) => Promise<string | false> }) {
  const { showToast } = useToast();
  const [limit, setLimit] = useState(100);
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [data, setData] = useState<LogData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    systemConsoleApi.log({ limit, errors: errorsOnly })
      .then((res) => { if (alive) setData(res.data); })
      .catch((e) => { if (alive) setError(apiErrorMessage(e, "Could not read the error log.")); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [limit, errorsOnly, attempt]);

  const clear = async () => {
    const ok = await ask("Clear the error log?", "Every entry in the log file is deleted. Download it first if you may need it.", null, "Clear log", true);
    if (ok === false) return;
    try {
      await systemConsoleApi.clearLog();
      showToast("Error log cleared", "success");
      setAttempt((n) => n + 1);
    } catch (e) {
      showToast(apiErrorMessage(e, "Could not clear the log."), "error");
    }
  };

  const download = async () => {
    try {
      await systemConsoleApi.downloadLog();
    } catch (e) {
      showToast(apiErrorMessage(e, "Could not download the log."), "error");
    }
  };

  const btn = "h-10 inline-flex items-center justify-center gap-1.5 px-3 rounded-xl border border-stone-200 bg-white text-sm font-medium text-stone-700 hover:bg-stone-50 disabled:opacity-50";

  return (
    <section className="bg-white rounded-xl border border-stone-200 p-4">
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 mb-3">
        <div className="flex items-center gap-2">
          <select value={limit} onChange={(e) => setLimit(Number(e.target.value))} aria-label="How many entries"
            className="h-10 px-3 rounded-xl border border-stone-200 bg-white text-sm text-stone-700 outline-none focus:border-amber-400">
            <option value={100}>Last 100 entries</option>
            <option value={300}>Last 300 entries</option>
            <option value={500}>Last 500 entries</option>
          </select>
          <label className="flex items-center gap-2 min-h-10 px-1 text-sm text-stone-600 cursor-pointer">
            <input type="checkbox" checked={errorsOnly} onChange={(e) => setErrorsOnly(e.target.checked)} className="w-4 h-4 accent-amber-600" />
            Errors only
          </label>
        </div>
        <div className="flex gap-2 sm:ml-auto">
          <button type="button" className={`${btn} flex-1 sm:flex-none`} onClick={() => setAttempt((n) => n + 1)} disabled={loading}>
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
          </button>
          <button type="button" className={`${btn} flex-1 sm:flex-none`} onClick={download}>
            <Download className="w-4 h-4" /> Download
          </button>
          <button type="button" className={`${btn} flex-1 sm:flex-none text-red-700`} onClick={clear}>
            <Trash2 className="w-4 h-4" /> Clear
          </button>
        </div>
      </div>

      {error ? <ErrorCard message={error} onRetry={() => setAttempt((n) => n + 1)} /> : !data ? <Skeleton rows={2} /> : (
        <>
          <p className="text-xs text-stone-500 mb-1 flex items-center gap-1.5">
            <FileText className="w-3.5 h-3.5" />
            {data.file ? `${data.file} · ${bytes(data.size)} · newest first` : "No log file yet."}
          </p>
          {data.entries.length === 0 ? (
            <p className="text-sm text-stone-400 py-8 text-center">{errorsOnly ? "No errors in the log." : "The log is empty."}</p>
          ) : (
            <ul className="divide-y divide-stone-100">
              {data.entries.map((e, i) => (
                <li key={`${e.time}-${i}`} className="py-2.5 flex gap-2.5">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded h-fit shrink-0 ${LEVEL_CLASS[e.level] ?? "bg-stone-100 text-stone-600"}`}>{e.level}</span>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-stone-800 break-words">{e.message}</p>
                    {e.time && <p className="text-xs text-stone-500">{e.time}</p>}
                    {e.detail && (
                      <details className="mt-1">
                        <summary className="text-xs font-semibold text-amber-700 cursor-pointer min-h-8 flex items-center">Details</summary>
                        <pre className="mt-1 bg-stone-900 text-stone-100 rounded-lg p-3 text-xs leading-relaxed whitespace-pre-wrap break-words max-h-96 overflow-auto">{e.detail}</pre>
                      </details>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </>
      )}
    </section>
  );
}

// ─── Output panel, dialog and small pieces ───────────────────────────────────

function OutputPanel({ output, running, onClose }: { output: Output; running: boolean; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(output.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard blocked: the text can still be selected */ }
  };
  const pre = useCallback((node: HTMLPreElement | null) => { if (node) node.scrollTop = node.scrollHeight; }, [output.text]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="fixed inset-x-0 bottom-0 z-40 flex justify-center pointer-events-none">
      <div className="pointer-events-auto w-full max-w-4xl bg-stone-900 text-stone-100 rounded-t-2xl shadow-2xl flex flex-col max-h-[60vh]" role="region" aria-label="Command output">
        <div className="flex items-center gap-2 pl-4 pr-2 py-2 border-b border-stone-800">
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold truncate">{output.title}</p>
            <p className="text-xs text-stone-400 truncate">{output.meta}</p>
          </div>
          <span className={`text-[11px] font-bold uppercase px-2 py-1 rounded-full shrink-0 ${
            output.state === "running" ? "bg-amber-900 text-amber-200" : output.state === "ok" ? "bg-emerald-900 text-emerald-200" : "bg-red-900 text-red-200"
          }`}>
            {output.state === "running" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : output.state === "ok" ? "Done" : "Failed"}
          </span>
          <button type="button" onClick={copy} className="h-10 w-10 inline-flex items-center justify-center rounded-lg hover:bg-stone-800" aria-label="Copy output" title="Copy output">
            {copied ? <Check className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
          </button>
          <button type="button" onClick={onClose} disabled={running} className="h-10 w-10 inline-flex items-center justify-center rounded-lg hover:bg-stone-800 disabled:opacity-40" aria-label="Close output" title={running ? "Wait for the command to finish" : "Close"}>
            <X className="w-4 h-4" />
          </button>
        </div>
        <pre ref={pre} aria-live="polite" className="flex-1 overflow-auto px-4 py-3 text-xs leading-relaxed whitespace-pre-wrap break-words font-mono">{output.text}</pre>
      </div>
    </div>
  );
}

function ConfirmDialog({ asking, onDone }: {
  asking: { title: string; text: string; word: string | null; ok: string; danger: boolean } | null;
  onDone: (value: string | false) => void;
}) {
  const [typed, setTyped] = useState("");
  useEffect(() => { setTyped(""); }, [asking]);
  const matches = !asking?.word || typed.trim().toUpperCase() === asking.word;

  return (
    <Modal
      isOpen={asking !== null}
      onClose={() => onDone(false)}
      title={asking?.title ?? ""}
      size="sm"
      footer={
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={() => onDone(false)} className="h-10 px-4 rounded-xl border border-stone-200 bg-white text-sm font-medium text-stone-700 hover:bg-stone-50">
            Cancel
          </button>
          <button
            type="button"
            disabled={!matches}
            onClick={() => onDone(asking?.word ? typed.trim().toUpperCase() : "yes")}
            className={`h-10 px-4 rounded-xl text-sm font-semibold text-white disabled:opacity-50 ${asking?.danger ? "bg-red-600 hover:bg-red-700" : "bg-amber-600 hover:bg-amber-700"}`}
          >
            {asking?.ok ?? "Run"}
          </button>
        </div>
      }
    >
      <p className="text-sm text-stone-600 whitespace-pre-line">{asking?.text}</p>
      {asking?.word && (
        <div className="mt-4">
          <label htmlFor="console-confirm" className="block text-xs font-semibold text-stone-600 mb-1">Type {asking.word} to confirm</label>
          <input
            id="console-confirm"
            value={typed}
            onChange={(e) => setTyped(e.target.value)}
            autoComplete="off"
            autoCapitalize="characters"
            spellCheck={false}
            className="w-full h-10 px-3 rounded-xl border border-stone-200 text-sm outline-none focus:border-amber-400"
          />
        </div>
      )}
    </Modal>
  );
}

function ErrorCard({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div role="alert" className="bg-white rounded-xl border border-red-200 p-6 flex flex-col items-center text-center gap-3">
      <AlertTriangle className="w-6 h-6 text-red-600" />
      <p className="text-sm text-stone-700">{message}</p>
      <button type="button" onClick={onRetry} className="h-10 inline-flex items-center gap-2 px-4 rounded-xl bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold">
        <RefreshCw className="w-4 h-4" /> Try again
      </button>
    </div>
  );
}

function Skeleton({ rows }: { rows: number }) {
  return (
    <div className="space-y-3 animate-pulse" aria-busy="true">
      {Array.from({ length: rows }).map((_, i) => <div key={i} className="h-28 rounded-xl bg-stone-100 border border-stone-200" />)}
    </div>
  );
}
